/**
 * Central nav / menu registry — extracted from Layout.jsx (Cycle 13.5).
 *
 * These maps are the single source of truth for what every menu key
 * points to and the emoji it renders. Keeping them in a dedicated
 * module lets us:
 *   • unit-test coverage (no key without a NAV_PATH / NAV_EMOJI entry)
 *   • re-use in the audit script (link + orphan report)
 *   • trim the 1,400-line Layout.jsx to just presentation logic
 *
 * Never import Layout from here — this file must stay dependency-free
 * so it can be pulled in by node scripts (like the audit) too.
 */

export const NAV_PATHS = {
  home: "/", countries: "/learn", globe: "/globe", continents: "/continents",
  // Cycle 60 (Feb 2026) · Phase-A nav restructure. Five action-based
  // primary destinations that absorb the old library grouping. Every
  // pre-existing module remains reachable — these keys just point the
  // primary tabs at the right hub for each action.
  //   • today   → child home + Continue + Recommended
  //   • learn   → Learn Topics hub (Learn Topics, Knowledge Hub, Academy)
  //   • play    → Games arcade (Games, Maths Academy practice, Pixel
  //               Art, Colouring, Code Quest, Quick Quiz)
  //   • explore → Globe hub (Globe, 195 countries, Wonders, Country of
  //               the Day, Live Quest)
  //   • rewards → Passport / stamps / badges / certificates / progress
  today:   "/",
  learn:   "/learn-topics",
  play:    "/games",
  explore: "/globe",
  rewards: "/passport",
  // Feb 2026 · Home Reshuffle — "Plan" primary tab. Read-only weekly
  // plan for the child; parent-gated view links deeper into
  // /parents/schedule with the CSV / term / summer / annual tools.
  plan:    "/plan",
  color: "/coloring", puzzles: "/puzzles", games: "/games", math: "/math",
  trip: "/trip", advanced: "/advanced", stickers: "/stickers", passport: "/passport",
  campaigns: "/campaigns", quiz: "/quiz", parents: "/parents", ask: "/ask",
  "dream-trip": "/dream-trip", "geo-lines": "/geo-lines",
  "maths-academy": "/academy/maths/library", knowledge: "/treasure-quest",
  "admin-lessons": "/admin/lessons",
  "admin-questions": "/admin/questions",
  // Cycle 53 · founder-only surfaces so the North Star / Approvals /
  // Outreach / Outcomes / Metrics-definitions pages are reachable from
  // the app instead of only via typed URL. PIN-gated in Layout.jsx.
  "admin-hub":           "/admin",
  "tools-hub":           "/tools",
  "admin-north-star":    "/admin/north-star",
  "admin-approvals":     "/admin/approvals",
  "admin-outreach":      "/admin/outreach-targets",
  "admin-pilot-ops":     "/admin/pilot-ops",
  "admin-fundraise":     "/admin/fundraise-targets",
  "admin-academy-coverage": "/admin/academy-coverage",
  "admin-outcomes":      "/admin/outcomes",
  "admin-metrics-defs":  "/admin/metrics-definitions",
  "my-quests":           "/live/host",
  "spelling-bee": "/spelling-bee",
  "treasure-quest": "/treasure-quest",
  "tic-tac-toe": "/tic-tac-toe",
  "games-hub":   "/games",
  "mandala": "/mandala",
  "stained-glass": "/stained-glass",
  "kaleidoscope": "/kaleidoscope",
  "name-place-animal": "/name-place-animal",
  "hub-learn": "/hub/learn", "hub-create": "/hub/create",
  "hub-play": "/hub/play", "hub-discover": "/hub/discover",
  languages: "/languages",
  "learn-topics": "/learn-topics",
  // Cycle 13.6 (Feb 2026) — internal key renamed from `my-world` to
  // `my-learning` so it matches the human-facing "My Learning" tab
  // label. Cycle 56 (Jul 2026) — path updated from `/my-world` to
  // `/my-learning` to align with the new marketing / product split.
  // The old `/my-world` remains for backward compatibility (see
  // App.js legacy redirect).
  "my-learning": "/my-learning",
  // Feb 2026 · The "Meet a Real Teacher" top-nav item + floating FAB
  // point at the Teacher Dashboard which now embeds a "Real Teacher"
  // hub linking families → directory, parents → their bookings, and
  // volunteers → the application flow. One button, all roles.
  "real-teachers": "/real-teachers",
  "play-home": "/?home=play",
  "quest-games": "/treasure-quest",
  animals: "/animals",
  "coloring": "/coloring",
  "pixel-art": "/coloring?mode=pixel",
  "flag-artist": "/coloring?mode=flag",
  "printables": "/printables",
  teacher: "/teacher",
  "teacher-console":    "/teachers/console",
  "homeschool-planner": "/homeschool/planner",
  "tutor-console":      "/tutor/console",
  schedule: "/parents/schedule",
  // Feb 2026 (Cycle 64.5) — Schools admin surface. Rendered in the
  // grown-up nav group so pastoral leads can jump straight to their
  // dashboard without hunting through /parents. Public /for-schools
  // pitch page lives at the "for-schools" key below.
  "schools":         "/schools/admin",
  "for-schools":     "/for-schools",
  // Feb 2026 (Cycle 64.6) — public curriculum browser.
  "curriculum":      "/curriculum",
  // Feb 2026 (Cycle 64.8) — Language Quest word-games module.
  "language-quest":  "/language-quest",
  "stories":         "/stories",
  "times-tables-quest": "/times-tables-quest",
  "english-quest":   "/english-assessment",
  "topic-geography": "/learn-topics?s=geography",
  "topic-maths":     "/learn-topics?s=maths",
  "topic-science":   "/learn-topics?s=science",
  "topic-english":   "/learn-topics?s=english",
  "topic-history":   "/learn-topics?s=history",
  "topic-languages": "/learn-topics?s=languages",
  "topic-art":       "/learn-topics?s=art",
  "topic-music":     "/learn-topics?s=music",
  "topic-ict":       "/learn-topics?s=ict",
  "topic-pe":        "/learn-topics?s=pe",
  "topic-re":        "/learn-topics?s=re",
  "topic-pshe":      "/learn-topics?s=pshe",
  "academy":        "/academy",
  "live-quest":     "/live",
  "code-breaker":   "/code-breaker",
  "everything":     "/everything",
  "learning-plan":  "/parents/schedule",
  "term-report":    "/parents/report",
  // Cycle 22B/C · Same-device 2-player duels (JindoBlu-inspired).
  // Kept as their own keys so they can appear in the mobile / desktop
  // menu directly — kids on tablets can jump to any duel in one tap
  // without hopping through the arcade.
  "arcade":          "/games",
  "duel-quiz":       "/games/duel/quiz",
  "duel-tables":     "/games/duel/tables",
  "duel-spell":      "/games/duel/spell",
  "duel-continent":  "/games/duel/continent",
  "duel-reaction":   "/games/duel/reaction",
  // Feb 2026 · Sign Language subject
  "sign-language":   "/learn/sign",
  "sign-language-asl": "/learn/sign/asl",
  "sign-match":      "/games/sign-match",
  "sign-sprint":     "/games/sign-sprint",
  // Feb 2026 · Phonics & Early Reading (Subject 1 flagship)
  "reading":         "/learn/reading",
  "phonics":         "/learn/reading",
  // Feb 2026 · Leveled Reading + Letters from Explorers + Draw-Along.
  // These land right in the Subjects drawer so kids and parents can
  // find every new reading surface without spelunking.
  "leveled-reading": "/reading",
  "letters":         "/reading",   // shares the reading home which surfaces the letter shelf
  "draw-along":      "/draw-along",
  // Feb 2026 · Subjects 3/5/6 launch
  "life-skills":     "/learn/life-skills",
  "money-time":      "/learn/money-time",
  "handwriting":     "/learn/handwriting",
  // Feb 2026 · Subjects 2 & 4 launch
  "typing":          "/games/key-quest",
  "key-quest":       "/games/key-quest",
  "code-quest":      "/learn/code-quest",
  "coding":          "/learn/code-quest",
  "learn-ai":        "/learn/ai",
  "ai-island":       "/learn/ai",
  // Jun 2026 · Kid Mode primary destinations + Grown-up Mode
  "learn-pathways":  "/learn-pathways",
  "play-create":     "/play-create",
  "my-quest":        "/my-quest",
  "grown-ups":       "/grown-ups",
  "portfolio":       "/portfolio",
  "curriculum-explorer": "/curriculum/explorer",
  "ai-safety":       "/ai-safety",
};

export const NAV_EMOJI = {
  home: "🏠", countries: "🗺️", globe: "🌍", continents: "🧭",
  // Phase-A primary tabs (Cycle 60)
  today:   "🏠",
  learn:   "📚",
  play:    "🎮",
  explore: "🌍",
  rewards: "🏆",
  plan:    "🗓️",
  color: "🎨", puzzles: "🧩", games: "🎮", math: "🧮",
  trip: "✈️", advanced: "🎓", stickers: "📔", passport: "🎒",
  campaigns: "🏆", quiz: "🧠", parents: "👨‍👩‍👧", ask: "✨",
  "dream-trip": "🗺️", "geo-lines": "🧭", "maths-academy": "🎓", knowledge: "🚀",
  "admin-lessons": "📝",
  "admin-questions": "❓",
  "admin-hub":          "🛡",
  "tools-hub":          "🧰",
  "admin-north-star":   "⭐",
  "admin-approvals":    "✍️",
  "admin-outreach":     "📮",
  "admin-pilot-ops":    "🏫",
  "admin-fundraise":    "💼",
  "admin-academy-coverage": "🎓",
  "admin-outcomes":     "📊",
  "admin-metrics-defs": "📖",
  "my-quests":          "📡",
  "spelling-bee": "🐝",
  "treasure-quest": "💰",
  "tic-tac-toe": "❌",
  "games-hub":   "🎯",
  "mandala": "🌀",
  "stained-glass": "🪟",
  "kaleidoscope": "🔮",
  "name-place-animal": "🎲",
  "hub-learn": "🌍", "hub-create": "🎨", "hub-play": "🎮", "hub-discover": "🏆",
  languages: "🗣️", "term-report": "📄", "learning-plan": "🗓️",
  "learn-topics": "📚", "my-learning": "🧭", "real-teachers": "🧑‍🏫", teacher: "🍎", schedule: "🗓️", "play-home": "🎪",
  "teacher-console": "🍎", "homeschool-planner": "🏠", "tutor-console": "🎓",
  "schools":     "🏫",
  "for-schools": "🏫",
  "curriculum":  "📚",
  "language-quest": "🔤",
  "stories": "📖",
  "times-tables-quest": "✖️",
  "english-quest": "🗣️",
  "quest-games": "🎯",
  animals: "🦁",
  "coloring": "🎨", "pixel-art": "🟪", "flag-artist": "🏳️", "printables": "🖨️",
  "topic-geography": "🌍", "topic-maths": "➗", "topic-science": "🔬",
  "topic-english": "📖", "topic-history": "🏛️", "topic-languages": "🗣️",
  "topic-art": "🎨", "topic-music": "🎵", "topic-ict": "💻",
  "topic-pe": "⚽", "topic-re": "🕊️", "topic-pshe": "💛",
  "academy": "🎓", "live-quest": "📡", "code-breaker": "🔐", "everything": "🧭",
  // 2-player duels
  "arcade":          "🕹️",
  "duel-quiz":       "🧠",
  "duel-tables":     "✖️",
  "duel-spell":      "🔤",
  "duel-continent":  "🌍",
  "duel-reaction":   "⚡",
  // Feb 2026 · Sign Language subject
  "sign-language":     "🤟",
  "sign-language-asl": "🤟",
  "sign-match":        "🎴",
  "sign-sprint":       "⚡",
  // Feb 2026 · Phonics
  "reading":           "📖",
  "leveled-reading":   "📚",
  "letters":           "📮",
  "draw-along":        "✏️",
  "phonics":           "📖",
  "life-skills":       "💛",
  "money-time":        "⏰",
  "handwriting":       "✍️",
  "typing":            "⌨️",
  "key-quest":         "⌨️",
  "code-quest":        "💻",
  "coding":            "💻",
  "learn-ai":          "🤖",
  "ai-island":         "🤖",
  "learn-pathways":    "📚",
  "play-create":       "🎮",
  "my-quest":          "🎒",
  "grown-ups":         "🧭",
  "portfolio":         "🛠️",
  "curriculum-explorer": "🗺️",
  "ai-safety":         "🛡️",
};

// Desktop persistent top-nav row + mobile bottom tab bar.
// Feb 2026 · Cycle 63 · Part 10 — canonical 5-item primary nav shared
// by every breakpoint per the "Menu & Navigation Improvement" command.
// Order per IA command P1.4: Today · Learn · Play · Explore · Me.
export const PRIMARY_KEYS = ["home", "learn-pathways", "play-create", "globe", "my-quest"];

// Emojis pulled from the home tiles so kids see the same colorful,
// familiar icon in the tabbar and on the homepage tile.
export const PILLAR_TAB_EMOJI = {
  explore:   "🌍",
  play:      "🎮",
  create:    "🎨",
  adventure: "🚀",
  "my-learning":"👤",
};
